import React, { Suspense, lazy, useState, useEffect, useMemo } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';
import { Layout, Menu, Drawer, Button, Spin } from 'antd';
import {
  DashboardOutlined,
  FundOutlined,
  PieChartOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuOutlined,
  AlertOutlined,
  ApartmentOutlined,
  TeamOutlined,
  FundProjectionScreenOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import Dashboard from '../pages/Dashboard';

// 懒加载各页面
const FundList = lazy(() => import('../pages/FundList'));
const ManagerList = lazy(() => import('../pages/ManagerList'));
const FundDetail = lazy(() => import('../pages/FundDetail'));
const ManagerDetail = lazy(() => import('../pages/ManagerDetail'));
const Compare = lazy(() => import('../pages/Compare'));
const PortfolioList = lazy(() => import('../pages/PortfolioList'));
const PortfolioEdit = lazy(() => import('../pages/PortfolioEdit'));
const PortfolioDetail = lazy(() => import('../pages/PortfolioDetail'));
const FofDeskValue = lazy(() => import('../pages/FofDeskValue'));
const FofOptimizerValue = lazy(() => import('../pages/FofOptimizerValue'));
const FofUniverse = lazy(() => import('../pages/FofUniverse'));
const FofPostInvestment = lazy(() => import('../pages/FofPostInvestment'));
const FofHub = lazy(() => import('../pages/FofHub'));
const MarketRegime = lazy(() => import('../pages/MarketRegime'));
const Users = lazy(() => import('../pages/Users'));
const WeeklyReview = lazy(() => import('../pages/WeeklyReview'));
const DistributionWeekly = lazy(() => import('../pages/DistributionWeekly'));
const DailyOpsAdmin = lazy(() => import('../pages/DailyOpsAdmin'));
const UnmatchedManagersAdmin = lazy(() => import('../pages/UnmatchedManagersAdmin'));
const InvestmentAnalysis = lazy(() => import('../pages/InvestmentAnalysis'));
const InvestmentAnalysisStrategyReturns = lazy(() => import('../pages/InvestmentAnalysisStrategyReturns'));
const QuantEquityCandidates = lazy(() => import('../pages/QuantEquityCandidates'));
const EvaluationHub = lazy(() => import('../pages/EvaluationHub'));
const ValuationL4 = lazy(() => import('../pages/ValuationL4'));
const CtaEnvV3Value = lazy(() => import('../pages/CtaEnvV3Value'));
const ReturnDistribution = lazy(() => import('../pages/ReturnDistribution'));

const { Sider, Content, Footer } = Layout;

// ============================================================
// 菜单配置：分组 + 二级菜单
// ============================================================
const MENU_DEFS = [
  // -------- 数据探索 --------
  {
    key: 'group-explore',
    label: '数据探索',
    type: 'group',
  },
  {
    key: 'dashboard',
    icon: <DashboardOutlined />,
    label: '总览',
    path: '/',
    permission: 'dashboard.view',
  },
  {
    key: 'funds',
    icon: <FundOutlined />,
    label: '基金',
    path: '/funds',
    permission: 'funds.view',
    children: [
      { key: 'funds-list', label: '基金列表', path: '/funds' },
      { key: 'funds-compare', label: '基金对比', path: '/compare' },
    ],
  },
  {
    key: 'managers',
    icon: <TeamOutlined />,
    label: '管理人',
    path: '/managers',
    permission: 'managers.view',
    children: [
      { key: 'managers-list', label: '管理人列表', path: '/managers' },
    ],
  },

  // -------- 策略研究 --------
  {
    key: 'group-strategy',
    label: '策略研究',
    type: 'group',
  },
  {
    key: 'investment-analysis',
    icon: <BarChartOutlined />,
    label: '投资分析',
    path: '/investment-analysis',
    permissionAny: [
      'investment_analysis.strategy_returns',
      'investment_analysis.index_enhancement',
      'investment_analysis.quant_select',
      'investment_analysis.quant_cta',
      'investment_analysis.market_neutral',
    'investment_analysis.arbitrage',
    'investment_analysis.cta_environment',
    'investment_analysis.quant_cta',
    'strategy_review.view',
  ],
  children: [
      { key: 'strategy-returns', label: '策略平均收益', path: '/investment-analysis/strategy-returns', permission: 'investment_analysis.strategy_returns' },
      { key: 'weekly-review', label: '策略回顾', path: '/investment-analysis/weekly-review', permission: 'strategy_review.view' },
      { key: 'evaluation-hub', label: '评价中心', path: '/evaluation-hub', permissionAny: [
        'investment_analysis.index_enhancement',
        'investment_analysis.quant_select',
        'investment_analysis.quant_cta',
        'investment_analysis.market_neutral',
        'investment_analysis.arbitrage',
      ] },
      { key: 'valuation-l4', label: '估值表', path: '/investment-analysis/valuation-l4', permission: 'investment_analysis.index_enhancement' },
      { key: 'cta-env-v3', label: 'CTA 环境', path: '/investment-analysis/cta-env-v3', permission: 'investment_analysis.cta_environment' },
      { key: 'return-distribution', label: '收益分布', path: '/return-distribution', permissionAny: [
        'investment_analysis.index_enhancement',
        'investment_analysis.quant_select',
        'investment_analysis.quant_cta',
        'investment_analysis.market_neutral',
        'investment_analysis.arbitrage',
      ] },
    ],
  },

  // -------- 代销跟踪 --------
  {
    key: 'group-distribution',
    label: '代销跟踪',
    type: 'group',
  },
  {
    key: 'distribution-weekly',
    icon: <BarChartOutlined />,
    label: '代销周度跟踪',
    path: '/distribution-weekly',
    permission: 'distribution_weekly.view',
  },

  // -------- 组合 & FOF --------
  {
    key: 'group-portfolio',
    label: '组合管理',
    type: 'group',
  },  {
    key: 'portfolio',
    icon: <PieChartOutlined />,
    label: '组合模拟',
    path: '/portfolio',
    permission: 'portfolio.view',
    children: [
      { key: 'portfolio-list', label: '组合列表', path: '/portfolio' },
    ],
  },
  {
    key: 'fof',
    icon: <FundProjectionScreenOutlined />,
    label: 'FOF',
    permission: 'fof_desk.view',
    children: [
      { key: 'fof-hub', label: 'FOF 管理总览', path: '/fof-hub' },
      { key: 'fof-desk', label: 'FOF 桌面', path: '/fof-desk' },
      { key: 'fof-optimizer', label: 'FOF 优化器', path: '/fof-optimizer' },
      { key: 'fof-post-investment', label: 'FOF 投后管理', path: '/fof-post-investment', permission: 'fof_desk.view' },
      { key: 'fof-universe', label: '最终入选池', path: '/fof-universe' },
      { key: 'market-regime', label: '市场环境与归因', path: '/market-regime' },
    ],
  },

  // -------- 后台管理 --------
  {
    key: 'group-admin',
    label: '系统管理',
    type: 'group',
  },
  {
    key: 'users',
    icon: <UserOutlined />,
    label: '用户管理',
    path: '/users',
    permission: 'users.view',
  },
  {
    key: 'daily-ops',
    icon: <AlertOutlined />,
    label: '每日运维',
    path: '/admin/daily-ops',
    permission: 'admin.daily_ops.view',
  },
  {
    key: 'unmatched-managers',
    icon: <ApartmentOutlined />,
    label: '管理人匹配',
    path: '/admin/unmatched-managers',
    permission: 'admin.unmatched_managers.view',
  },
];

function RequirePermission({ anyOf, permission, children }) {
  const { hasPermission, hasAnyPermission } = useAuth();
  const allowed = anyOf ? hasAnyPermission(anyOf) : hasPermission(permission);
  if (!allowed) return <Navigate to="/" replace />;
  return children;
}

// 兼容旧链接 /investment-analysis/strategy-detail/:level2 → /return-distribution/:level2
function StrategyDetailRedirect() {
  const { level2 } = useParams();
  return <Navigate to={`/return-distribution/${encodeURIComponent(level2 || '')}`.replace(/\/$/, '')} replace />;
}

function RouteFallback() {
  return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <Spin size="large" />
    </div>
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return isMobile;
}

// 根据当前路径，展开对应的 submenu keys
function getOpenKeys(pathname) {
  const openKeys = [];
  MENU_DEFS.forEach((item) => {
    if (item.children) {
      const matched = item.children.find((child) => pathname.startsWith(child.path));
      if (matched) openKeys.push(item.key);
    }
  });
  return openKeys;
}

// 根据路径找当前选中 key
function getSelectedKey(pathname) {
  for (const item of MENU_DEFS) {
    if (item.path && pathname === item.path) return item.key;
    if (item.children) {
      const matched = item.children.find((c) => pathname.startsWith(c.path));
      if (matched) return matched.key;
    }
  }
  return 'dashboard';
}

// 把 MENU_DEFS 转成 Ant Menu items
function buildMenuItems(menuDefs, hasPermissionFn, hasAnyPermissionFn, navigateFn, onCloseDrawer) {
  const items = [];

  menuDefs.forEach((item) => {
    // group 分隔符
    if (item.type === 'group') {
      items.push({
        key: `group-${item.key}`,
        type: 'group',
        label: item.label,
      });
      return;
    }

    // 权限过滤
    const hasAccess = item.permission
      ? hasPermissionFn(item.permission)
      : item.permissionAny
      ? hasAnyPermissionFn(item.permissionAny)
      : true;
    if (!hasAccess) return;

    // 构建菜单项
    if (item.children) {
      const subItems = item.children
        .filter((child) => {
          // 子菜单权限：优先用子项自己的 permission/permissionAny，否则跟随父级
          if (child.permission) return hasPermissionFn(child.permission);
          if (child.permissionAny) return hasAnyPermissionFn(child.permissionAny);
          return hasAccess;
        })
        .map((child) => ({
          key: child.key,
          label: child.label,
          onClick: () => {
            navigateFn(child.path);
            if (onCloseDrawer) onCloseDrawer();
          },
        }));

      items.push({
        key: item.key,
        icon: item.icon,
        label: item.label,
        children: subItems,
      });
    } else {
      items.push({
        key: item.key,
        icon: item.icon,
        label: item.label,
        onClick: () => {
          navigateFn(item.path);
          if (onCloseDrawer) onCloseDrawer();
        },
      });
    }
  });

  return items;
}

export default function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasPermission, hasAnyPermission } = useAuth();
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const selectedKey = getSelectedKey(location.pathname);
  const openKeys = getOpenKeys(location.pathname);

  const menuItems = useMemo(
    () => buildMenuItems(MENU_DEFS, hasPermission, hasAnyPermission, navigate, isMobile ? () => setDrawerOpen(false) : null),
    [hasPermission, hasAnyPermission, navigate, isMobile]
  );

  const userRoleLabel =
    user?.role === 'system_admin' ? '系统管理员' : user?.role === 'admin' ? '管理员' : '用户';

  // 移动端菜单项
  const mobileMenuItems = useMemo(
    () => buildMenuItems(MENU_DEFS, hasPermission, hasAnyPermission, navigate, () => setDrawerOpen(false)),
    [hasPermission, hasAnyPermission, navigate]
  );

  // 路由宽内容判断
  const isWideContent =
    location.pathname === '/funds' ||
    location.pathname.startsWith('/funds?') ||
    location.pathname === '/managers' ||
    location.pathname.startsWith('/managers?') ||
    location.pathname === '/weekly-review' ||
    location.pathname.startsWith('/weekly-review?') ||
    location.pathname === '/investment-analysis' ||
    location.pathname.startsWith('/investment-analysis/') ||
    location.pathname.startsWith('/investment-analysis?') ||
    location.pathname.startsWith('/manager/') ||
    location.pathname === '/fof-desk' ||
    location.pathname === '/fof-optimizer' ||
    location.pathname.startsWith('/fof-post-investment') ||
    location.pathname.startsWith('/return-distribution') ||
    // 代销周度跟踪：表格列多 + 每组一张走势图，窄容器会导致横向溢出
    location.pathname === '/distribution-weekly' ||
    location.pathname.startsWith('/distribution-weekly?');

  // 路由白名单（非全宽页面）
  const isFullWidth =
    location.pathname === '/compare' ||
    location.pathname.startsWith('/portfolio/') ||
    location.pathname === '/portfolio';

  const iaAny = [
    'investment_analysis.strategy_returns',
    'investment_analysis.index_enhancement',
    'investment_analysis.quant_select',
    'investment_analysis.quant_cta',
    'investment_analysis.market_neutral',
  'investment_analysis.arbitrage',
  'investment_analysis.cta_environment',
  'investment_analysis.quant_cta',
  'strategy_review.view',
];

  // 非移动端渲染侧栏
  if (!isMobile) {
    return (
      <Layout className="app-layout" style={{ minHeight: '100vh', position: 'relative' }}>
        <Sider
          className="app-sider"
          width={220}
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          trigger={null}
          theme="dark"
        >
          {/* Logo */}
          <div className="app-sider-logo">
            <span className="app-sider-logo-text">
              {collapsed ? '📊' : '🏦 私募数据平台'}
            </span>
          </div>

          {/* 用户信息 */}
          {!collapsed && (
            <div className="app-sider-user">
              <div className="app-sider-user-name">{user?.username}</div>
              <div className="app-sider-user-role">{userRoleLabel}</div>
              <Button
                type="text"
                size="small"
                icon={<LogoutOutlined />}
                className="app-sider-logout"
                onClick={logout}
              >
                退出登录
              </Button>
            </div>
          )}

          {/* 导航菜单 */}
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selectedKey]}
            defaultOpenKeys={openKeys}
            items={menuItems}
            inlineCollapsed={collapsed}
            style={{ flex: 1, overflowY: 'auto' }}
          />

          {/* 折叠按钮 */}
          <div
            style={{
              padding: '12px 0',
              textAlign: 'center',
              borderTop: '1px solid rgba(255,255,255,0.08)',
              cursor: 'pointer',
              color: 'rgba(255,255,255,0.45)',
              fontSize: 16,
              userSelect: 'none',
            }}
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? '▶' : '◀'}
          </div>
        </Sider>

        {/* 主内容区 */}
        <Layout style={{ flex: 1, minWidth: 0, transition: 'all 0.2s', marginLeft: collapsed ? 80 : 220 }}>
          <Content className="app-content">
            <div className={isWideContent && !isFullWidth ? 'app-inner-wide' : 'app-inner'}>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<RequirePermission permission="dashboard.view"><Dashboard /></RequirePermission>} />
                  <Route path="/funds" element={<RequirePermission permission="funds.view"><FundList /></RequirePermission>} />
                  <Route path="/managers" element={<RequirePermission permission="managers.view"><ManagerList /></RequirePermission>} />
                  <Route path="/fund/:code" element={<RequirePermission permission="funds.view"><FundDetail /></RequirePermission>} />
                  <Route path="/manager/:managerId" element={<RequirePermission permission="managers.view"><ManagerDetail /></RequirePermission>} />
                  <Route path="/compare" element={<RequirePermission permission="compare.run"><Compare /></RequirePermission>} />
                  <Route path="/portfolio" element={<RequirePermission permission="portfolio.view"><PortfolioList /></RequirePermission>} />
                  <Route path="/portfolio/new" element={<RequirePermission permission="portfolio.create"><PortfolioEdit /></RequirePermission>} />
                  <Route path="/portfolio/:id" element={<RequirePermission permission="portfolio.view"><PortfolioDetail /></RequirePermission>} />
                  <Route path="/fof-desk" element={<RequirePermission permission="fof_desk.view"><FofDeskValue /></RequirePermission>} />
                  <Route path="/fof-hub" element={<RequirePermission permission="fof_desk.view"><FofHub /></RequirePermission>} />
                  <Route path="/fof-universe" element={<RequirePermission permission="fof_desk.view"><FofUniverse /></RequirePermission>} />
                  <Route path="/fof-optimizer" element={<RequirePermission permission="fof_desk.view"><FofOptimizerValue /></RequirePermission>} />
                  <Route path="/fof-post-investment" element={<RequirePermission permission="fof_desk.view"><FofPostInvestment /></RequirePermission>} />
                  <Route path="/market-regime" element={<RequirePermission permission="fof_desk.view"><MarketRegime /></RequirePermission>} />
                  <Route path="/weekly-review" element={<RequirePermission permission="strategy_review.view"><WeeklyReview /></RequirePermission>} />
                  <Route path="/distribution-weekly" element={<RequirePermission permission="distribution_weekly.view"><DistributionWeekly /></RequirePermission>} />
                  <Route path="/investment-analysis" element={<RequirePermission anyOf={iaAny}><InvestmentAnalysis /></RequirePermission>}>
                    <Route index element={<Navigate to="strategy-returns" replace />} />
                    <Route path="strategy-returns" element={<RequirePermission permission="investment_analysis.strategy_returns"><InvestmentAnalysisStrategyReturns /></RequirePermission>} />
                    <Route path="weekly-review" element={<RequirePermission permission="strategy_review.view"><WeeklyReview /></RequirePermission>} />
                    <Route path="quant-equity" element={<RequirePermission anyOf={['investment_analysis.index_enhancement', 'investment_analysis.quant_select', 'investment_analysis.quant_cta', 'investment_analysis.market_neutral', 'investment_analysis.arbitrage']}><QuantEquityCandidates /></RequirePermission>} />
                    <Route path="index-enhancement" element={<Navigate to="/evaluation-hub" replace />} />
                    <Route path="quant-select" element={<Navigate to="/evaluation-hub" replace />} />
                    <Route path="quant-cta" element={<Navigate to="/evaluation-hub" replace />} />
                    <Route path="quant-mn" element={<Navigate to="/evaluation-hub" replace />} />
                    <Route path="quant-arb" element={<Navigate to="/evaluation-hub" replace />} />
                    <Route path="valuation-l4" element={<RequirePermission permission="investment_analysis.index_enhancement"><ValuationL4 /></RequirePermission>} />
                    <Route path="strategy-detail/:level2" element={<RequirePermission permission="investment_analysis.index_enhancement"><StrategyDetailRedirect /></RequirePermission>} />
                    <Route path="index-enhancement-value" element={<Navigate to="/investment-analysis/index-enhancement" replace />} />
                    <Route path="alpha-quality" element={<Navigate to="/investment-analysis/index-enhancement" replace />} />
                    <Route path="market-neutral-value" element={<Navigate to="/investment-analysis/quant-mn" replace />} />
                    <Route path="cta-env-v3" element={<RequirePermission permission="investment_analysis.cta_environment"><CtaEnvV3Value /></RequirePermission>} />
                    <Route path="cta-wx" element={<Navigate to="/investment-analysis/cta-env-v3" replace />} />
                    <Route path="cta-environment-value" element={<Navigate to="/investment-analysis/cta-env-v3" replace />} />
                    <Route path="cta-ei-v1" element={<Navigate to="/investment-analysis/cta-env-v3" replace />} />
                    <Route path="cta-env-v2" element={<Navigate to="/investment-analysis/cta-env-v3" replace />} />
                    <Route path="quant-cta-value" element={<Navigate to="/evaluation-hub" replace />} />
                  </Route>
                  <Route path="/evaluation-hub" element={<RequirePermission anyOf={iaAny}><EvaluationHub /></RequirePermission>} />
                  {/* 收益分布（顶级页面）：支持 /return-distribution 与 /return-distribution/:level2 */}
                  <Route path="/return-distribution" element={<RequirePermission anyOf={['investment_analysis.index_enhancement', 'investment_analysis.quant_select', 'investment_analysis.quant_cta', 'investment_analysis.market_neutral', 'investment_analysis.arbitrage']}><ReturnDistribution /></RequirePermission>} />
                  <Route path="/return-distribution/:level2" element={<RequirePermission anyOf={['investment_analysis.index_enhancement', 'investment_analysis.quant_select', 'investment_analysis.quant_cta', 'investment_analysis.market_neutral', 'investment_analysis.arbitrage']}><ReturnDistribution /></RequirePermission>} />
                  <Route path="/users" element={<RequirePermission permission="users.view"><Users /></RequirePermission>} />
                  <Route path="/admin/daily-ops" element={<RequirePermission permission="admin.daily_ops.view"><DailyOpsAdmin /></RequirePermission>} />
                  <Route path="/admin/unmatched-managers" element={<RequirePermission permission="admin.unmatched_managers.view"><UnmatchedManagersAdmin /></RequirePermission>} />
                </Routes>
              </Suspense>
            </div>
          </Content>
          <Footer className="app-footer" style={{ transition: 'margin-left 0.2s' }}>私募数据平台</Footer>
        </Layout>
      </Layout>
    );
  }

  // ========== 移动端：顶部栏 + 抽屉 ==========
  return (
    <Layout className="app-layout">
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 56,
          background: '#001529',
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
          zIndex: 100,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}
      >
        <Button
          type="text"
          icon={<MenuOutlined style={{ color: '#fff', fontSize: 18 }} />}
          onClick={() => setDrawerOpen(true)}
          aria-label="打开菜单"
        />
        <span style={{ color: '#fff', fontSize: 15, fontWeight: 600, marginLeft: 12 }}>
          🏦 私募数据平台
        </span>
        <div style={{ flex: 1 }} />
        <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>
          {user?.username} ({userRoleLabel})
        </span>
      </div>

      <Drawer
        title={null}
        placement="left"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        width={280}
        styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column' } }}
        headerStyle={{ display: 'none' }}
      >
        <div
          style={{
            padding: '16px 20px',
            background: '#001529',
            color: '#fff',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600 }}>🏦 私募数据平台</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>
            {user?.username} · {userRoleLabel}
          </div>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          defaultOpenKeys={openKeys}
          items={mobileMenuItems}
          style={{ flex: 1, overflowY: 'auto', borderRight: 0 }}
        />
        <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Button
            type="text"
            icon={<LogoutOutlined />}
            onClick={logout}
            style={{ color: 'rgba(255,255,255,0.6)', padding: 0, height: 'auto' }}
          >
            退出登录
          </Button>
        </div>
      </Drawer>

      <Content className="app-content" style={{ marginLeft: 0, paddingTop: 56 }}>
        <div className={isWideContent && !isFullWidth ? 'app-inner-wide' : 'app-inner'}>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<RequirePermission permission="dashboard.view"><Dashboard /></RequirePermission>} />
              <Route path="/funds" element={<RequirePermission permission="funds.view"><FundList /></RequirePermission>} />
              <Route path="/managers" element={<RequirePermission permission="managers.view"><ManagerList /></RequirePermission>} />
              <Route path="/fund/:code" element={<RequirePermission permission="funds.view"><FundDetail /></RequirePermission>} />
              <Route path="/manager/:managerId" element={<RequirePermission permission="managers.view"><ManagerDetail /></RequirePermission>} />
              <Route path="/compare" element={<RequirePermission permission="compare.run"><Compare /></RequirePermission>} />
              <Route path="/portfolio" element={<RequirePermission permission="portfolio.view"><PortfolioList /></RequirePermission>} />
              <Route path="/portfolio/new" element={<RequirePermission permission="portfolio.create"><PortfolioEdit /></RequirePermission>} />
              <Route path="/portfolio/:id" element={<RequirePermission permission="portfolio.view"><PortfolioDetail /></RequirePermission>} />
              <Route path="/fof-desk" element={<RequirePermission permission="fof_desk.view"><FofDeskValue /></RequirePermission>} />
              <Route path="/fof-universe" element={<RequirePermission permission="fof_desk.view"><FofUniverse /></RequirePermission>} />
              <Route path="/fof-optimizer" element={<RequirePermission permission="fof_desk.view"><FofOptimizerValue /></RequirePermission>} />
              <Route path="/market-regime" element={<RequirePermission permission="fof_desk.view"><MarketRegime /></RequirePermission>} />
              <Route path="/weekly-review" element={<RequirePermission permission="strategy_review.view"><WeeklyReview /></RequirePermission>} />
              <Route path="/distribution-weekly" element={<RequirePermission permission="distribution_weekly.view"><DistributionWeekly /></RequirePermission>} />
              <Route path="/investment-analysis" element={<RequirePermission anyOf={iaAny}><InvestmentAnalysis /></RequirePermission>}>
                <Route index element={<Navigate to="strategy-returns" replace />} />
                <Route path="strategy-returns" element={<RequirePermission permission="investment_analysis.strategy_returns"><InvestmentAnalysisStrategyReturns /></RequirePermission>} />
                <Route path="quant-equity" element={<RequirePermission anyOf={['investment_analysis.index_enhancement', 'investment_analysis.quant_select', 'investment_analysis.quant_cta', 'investment_analysis.market_neutral', 'investment_analysis.arbitrage']}><QuantEquityCandidates /></RequirePermission>} />
                <Route path="index-enhancement" element={<Navigate to="/evaluation-hub" replace />} />
                <Route path="quant-select" element={<Navigate to="/evaluation-hub" replace />} />
                <Route path="quant-cta" element={<Navigate to="/evaluation-hub" replace />} />
                <Route path="quant-mn" element={<Navigate to="/evaluation-hub" replace />} />
                <Route path="quant-arb" element={<Navigate to="/evaluation-hub" replace />} />
                <Route path="valuation-l4" element={<RequirePermission permission="investment_analysis.index_enhancement"><ValuationL4 /></RequirePermission>} />
                <Route path="index-enhancement-value" element={<Navigate to="/evaluation-hub" replace />} />
                <Route path="alpha-quality" element={<Navigate to="/evaluation-hub" replace />} />
                <Route path="market-neutral-value" element={<Navigate to="/evaluation-hub" replace />} />
                <Route path="cta-env-v3" element={<RequirePermission permission="investment_analysis.cta_environment"><CtaEnvV3Value /></RequirePermission>} />
                <Route path="cta-wx" element={<Navigate to="/investment-analysis/cta-env-v3" replace />} />
                <Route path="cta-environment-value" element={<Navigate to="/investment-analysis/cta-env-v3" replace />} />
                <Route path="cta-ei-v1" element={<Navigate to="/investment-analysis/cta-env-v3" replace />} />
                <Route path="cta-env-v2" element={<Navigate to="/investment-analysis/cta-env-v3" replace />} />
                <Route path="quant-cta-value" element={<Navigate to="/evaluation-hub" replace />} />
              </Route>
              <Route path="/return-distribution" element={<RequirePermission anyOf={['investment_analysis.index_enhancement', 'investment_analysis.quant_select', 'investment_analysis.quant_cta', 'investment_analysis.market_neutral', 'investment_analysis.arbitrage']}><ReturnDistribution /></RequirePermission>} />
              <Route path="/return-distribution/:level2" element={<RequirePermission anyOf={['investment_analysis.index_enhancement', 'investment_analysis.quant_select', 'investment_analysis.quant_cta', 'investment_analysis.market_neutral', 'investment_analysis.arbitrage']}><ReturnDistribution /></RequirePermission>} />
              <Route path="/users" element={<RequirePermission permission="users.view"><Users /></RequirePermission>} />
              <Route path="/admin/daily-ops" element={<RequirePermission permission="admin.daily_ops.view"><DailyOpsAdmin /></RequirePermission>} />
              <Route path="/admin/unmatched-managers" element={<RequirePermission permission="admin.unmatched_managers.view"><UnmatchedManagersAdmin /></RequirePermission>} />
            </Routes>
          </Suspense>
        </div>
      </Content>
      <Footer className="app-footer">私募数据平台</Footer>
    </Layout>
  );
}
